/**
 * Attribution capture — reads UTM / click-id / Media* / referrer / landing
 * context from the browser and persists the *first-touch* values in
 * localStorage (90-day TTL) so that lead-form submissions consistently carry
 * the original campaign attribution even if the user navigates around or
 * comes back days later in a new session.
 *
 * Storage rationale:
 *   - localStorage (not sessionStorage) so attribution survives tab close.
 *   - 90-day TTL matches Google Analytics' default attribution window.
 *   - First-touch wins: once captured, subsequent visits do NOT overwrite.
 *
 * Fields captured:
 *   - UTM family: utm_source, utm_medium, utm_campaign, utm_term, utm_content
 *   - Click IDs:  gclid, fbclid, ttclid, msclkid, gad_source, gad_campaignid
 *   - CampusLogin media taxonomy: MediaGroupID, MediaID  ← NEW (2026-04-29)
 *   - Page context: referrer, landing_url
 */
const ATTR_KEY = "ac_attribution_first_touch";
const TTL_DAYS = 90;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

// All URL params we want to snapshot. CampusLogin's MediaGroupID / MediaID
// arrive as ad-network passthrough and drive paid-vs-organic segmentation
// in the CRM — so capturing them is non-negotiable for accurate reporting.
const ATTR_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "ttclid",
  "msclkid",
  "gad_source",
  "gad_campaignid",
  "MediaGroupID",
  "MediaID",
];

// Case-insensitive lookup so `?mediagroupid=…` (lowercased by some ad platforms)
// still matches `MediaGroupID`. Returns the first matching value, or null.
function getParamCI(params, name) {
  const wanted = name.toLowerCase();
  for (const [k, v] of params.entries()) {
    if (k.toLowerCase() === wanted) return v;
  }
  return null;
}

function readFirstTouch() {
  try {
    const raw = localStorage.getItem(ATTR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire if older than TTL
    if (parsed.captured_at) {
      const age = Date.now() - new Date(parsed.captured_at).getTime();
      if (age > TTL_MS) {
        localStorage.removeItem(ATTR_KEY);
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistFirstTouch(data) {
  try {
    localStorage.setItem(ATTR_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota errors */
  }
}

/** Call once on app mount to snapshot first-touch attribution. */
export function captureFirstTouchOnMount() {
  if (typeof window === "undefined") return;
  const existing = readFirstTouch();
  if (existing) return; // first-touch already captured (within TTL window)

  const params = new URLSearchParams(window.location.search);
  const snapshot = { captured_at: new Date().toISOString() };
  ATTR_FIELDS.forEach((k) => {
    const v = getParamCI(params, k);
    if (v) snapshot[k] = v;
  });
  snapshot.referrer = document.referrer || "";
  snapshot.landing_url = window.location.href;
  persistFirstTouch(snapshot);
}

/** Build the attribution payload sent with each form submission. */
export function buildAttributionPayload(extra = {}) {
  if (typeof window === "undefined") return {};
  const firstTouch = readFirstTouch() || {};
  const currentParams = new URLSearchParams(window.location.search);
  const attr = { ...firstTouch };

  // If first-touch didn't capture a field but the *current* URL has it,
  // use the current value. Helps when a user lands organically then arrives
  // at a deep page from an ad — we still grab the ad attribution.
  ATTR_FIELDS.forEach((k) => {
    if (!attr[k]) {
      const v = getParamCI(currentParams, k);
      if (v) attr[k] = v;
    }
  });

  attr.page_url = window.location.href;
  attr.page_slug = window.location.pathname;
  return { ...attr, ...extra };
}
